import React, { useState } from "react";
import {
  Option,
  RentControlTypesFormLimit,
  RentControlTypesFormRentControlType,
  DefaultsPageConfiguration
} from "api";
import createDataTable, {
  IPagedTableState,
  ColumnDefinition,
  DataTableProps
} from "components/controls/createTable";

import Dropdown from "components/controls/Dropdown";
import Input from "components/controls/Input";
import ValidationWrapper from "components/controls/ValidationWrapper";
import listUtils from "utils/listUtils";
import { Calendar } from "primereact/calendar";
import { parse, format } from "date-fns";

export interface RentControlTypeLimitsFormTableProps {
  limits?: RentControlTypesFormLimit[] | undefined;
  rentControlTypes: RentControlTypesFormRentControlType[];
  onChange: (limits: RentControlTypesFormLimit[]) => void;
  onRemoveRowAction?:
    | ((rowToRemove: RentControlTypesFormLimit) => void)
    | undefined;
  onAddRowAction?: (() => void) | undefined;
  pageConfiguration: DefaultsPageConfiguration | undefined;
}

interface TableState extends IPagedTableState {}

const noLimitLabel = "No limit";
const noCapLabel = "No cap";
const anyEffectiveDateLabel = "Any date";
const noneOption: Option = { key: "none", displayValue: "None" };

const RentControlTypeLimitsFormTable: React.FC<RentControlTypeLimitsFormTableProps> =
  function (props: RentControlTypeLimitsFormTableProps) {
    const [table, setTable] = useState<TableState>({
      offset: 0,
      limit: 10
    });

    const [selectedYear, setSelectedYear] = useState<Option | undefined>(
      undefined
    );

    const isEditEnabled = !!(
      props.pageConfiguration && props.pageConfiguration.isEditEnabled
    );

    const yearOptions: Option[] = (() => {
      const years = new Set<string>();
      (props.limits || []).forEach((l) => {
        if (l.effectiveDate) {
          const parts = l.effectiveDate.split("/");
          if (parts.length === 3) years.add(parts[2]);
        }
      });
      const options: Option[] = Array.from(years)
        .sort()
        .map((year) => ({ key: year, displayValue: year }));
      const hasNoDates = (props.limits || []).some((l) => !l.effectiveDate);
      if (hasNoDates) options.push(noneOption);
      return options;
    })();

    const filteredLimits = selectedYear
      ? (props.limits || []).filter((l) => {
          if (selectedYear.key === noneOption.key) return !l.effectiveDate;
          const parts = (l.effectiveDate || "").split("/");
          return parts.length === 3 && parts[2] === selectedYear.key;
        })
      : props.limits;

    const norm = (value: string | null | undefined) => value || undefined;

    const rowHasChanged = (
      oldRow: RentControlTypesFormLimit,
      newRow: RentControlTypesFormLimit
    ): boolean =>
      oldRow.selectedRentControlType?.key !==
        newRow.selectedRentControlType?.key ||
      norm(oldRow.effectiveDate) !== norm(newRow.effectiveDate) ||
      norm(oldRow.maxPercentageByRevenueManager) !==
        norm(newRow.maxPercentageByRevenueManager) ||
      norm(oldRow.maxPercentageByLaw) !== norm(newRow.maxPercentageByLaw) ||
      norm(oldRow.increasesAllowedInFirstTwelveMonths) !==
        norm(newRow.increasesAllowedInFirstTwelveMonths) ||
      norm(oldRow.increasesAllowedAfterTwelveMonths) !==
        norm(newRow.increasesAllowedAfterTwelveMonths);

    const onRowChanged = (
      oldRow: RentControlTypesFormLimit,
      newRow: RentControlTypesFormLimit
    ) => {
      if (!props.limits) {
        return;
      }

      if (!rowHasChanged(oldRow, newRow)) {
        return;
      }

      newRow.isChanged = true;

      props.onChange(
        listUtils.copyListWithNewItem(props.limits, oldRow, newRow)
      );
    };

    const getRentControlTypeBody = function (rowData: any) {
      const limit = rowData as RentControlTypesFormLimit;

      if (!isEditEnabled) {
        return (
          <div>
            {limit.selectedRentControlType &&
              limit.selectedRentControlType.displayValue}
          </div>
        );
      }

      return (
        <Dropdown
          onChange={(
            selectedRentControlType:
              | RentControlTypesFormRentControlType
              | undefined
          ) =>
            onRowChanged(limit, {
              ...limit,
              selectedRentControlType: selectedRentControlType
            })
          }
          selectedValue={limit.selectedRentControlType}
          options={props.rentControlTypes}
          style={{ minWidth: "100%", maxWidth: "100%" }}
          validationMessage={
            limit.fieldValidationErrors &&
            limit.fieldValidationErrors["SelectedRentControlType"]
          }
        />
      );
    };

    const getEffectiveDateBody = function (rowData: any) {
      const limit = rowData as RentControlTypesFormLimit;

      if (!isEditEnabled) {
        return <div>{limit.effectiveDate || anyEffectiveDateLabel}</div>;
      }

      const parsedDate = limit.effectiveDate
        ? parse(limit.effectiveDate, "M/d/yyyy", new Date())
        : undefined;

      return (
        <ValidationWrapper
          showErrorState={
            limit.fieldValidationErrors != null &&
            limit.fieldValidationErrors["EffectiveDate"] != null
          }
        >
          <Calendar
            value={parsedDate}
            tooltip={
              limit.fieldValidationErrors &&
              limit.fieldValidationErrors["EffectiveDate"]
            }
            readOnlyInput={true}
            onChange={(e) => {
              onRowChanged(limit, {
                ...limit,
                effectiveDate: e.value
                  ? format(e.value as Date, "M/d/yyyy")
                  : undefined
              });
            }}
            showButtonBar
            appendTo={document.getElementById("root")}
          />
        </ValidationWrapper>
      );
    };

    const getPercentageBody = function (
      field: keyof RentControlTypesFormLimit,
      validationField: string
    ) {
      return function (rowData: any) {
        const limit = rowData as RentControlTypesFormLimit;
        const value = limit[field] as string | undefined;

        if (!isEditEnabled) {
          return <div>{value || noCapLabel}</div>;
        }

        return (
          <Input
            value={value}
            validationMessage={
              limit.fieldValidationErrors &&
              limit.fieldValidationErrors[validationField]
            }
            onUnfocus={(newValue: string) => {
              onRowChanged(limit, {
                ...limit,
                [field]: newValue || undefined
              });
            }}
          />
        );
      };
    };

    const getNumberOfIncreasesBody = function (
      field: keyof RentControlTypesFormLimit,
      validationField: string
    ) {
      return function (rowData: any) {
        const limit = rowData as RentControlTypesFormLimit;
        const value = limit[field] as string | undefined;

        if (!isEditEnabled) {
          return <div>{value || noLimitLabel}</div>;
        }

        return (
          <Input
            value={value}
            validationMessage={
              limit.fieldValidationErrors &&
              limit.fieldValidationErrors[validationField]
            }
            onUnfocus={(newValue: string) => {
              onRowChanged(limit, {
                ...limit,
                [field]: newValue || undefined
              });
            }}
          />
        );
      };
    };

    const getColumns = function (): any[] {
      const columns = [] as ColumnDefinition<
        RentControlTypesFormLimit,
        keyof RentControlTypesFormLimit
      >[];

      return columns.concat([
        {
          header: "Rent control",
          style: { width: "20%" },
          body: getRentControlTypeBody
        },
        {
          field: "effectiveDate",
          header: "Effective date",
          style: { width: "12%", textAlign: "center" },
          body: getEffectiveDateBody
        },
        {
          field: "maxPercentageByRevenueManager",
          header: "Max % by RM",
          style: { width: "10%", textAlign: "center" },
          body: getPercentageBody(
            "maxPercentageByRevenueManager",
            "MaxPercentageByRevenueManager"
          )
        },
        {
          field: "maxPercentageByLaw",
          header: "Max % by law",
          style: { width: "10%", textAlign: "center" },
          body: getPercentageBody("maxPercentageByLaw", "MaxPercentageByLaw")
        },
        {
          field: "increasesAllowedInFirstTwelveMonths",
          header: "# Increases allowed in the first 12 months",
          style: { width: "14%", textAlign: "center" },
          body: getNumberOfIncreasesBody(
            "increasesAllowedInFirstTwelveMonths",
            "IncreasesAllowedInFirstTwelveMonths"
          )
        },
        {
          field: "increasesAllowedAfterTwelveMonths",
          header:
            "# Increases allowed after 12 months within a 12 month period",
          style: { width: "18%", textAlign: "center" },
          body: getNumberOfIncreasesBody(
            "increasesAllowedAfterTwelveMonths",
            "IncreasesAllowedAfterTwelveMonths"
          )
        },
        {
          field: "lastUpdatedDate",
          header: "Last updated date",
          style: { width: "10%", textAlign: "center" }
        },
        {
          field: "lastUpdatedBy",
          header: "Last updated by",
          style: { width: "10%", textAlign: "center" }
        }
      ]);
    };

    const tableProps: DataTableProps<RentControlTypesFormLimit, TableState> = {
      value: filteredLimits || undefined,
      editMode: "cell",
      state: table,
      updateState: setTable,
      onAddRowAction: props.onAddRowAction,
      /*
       * Any row can be removed. A batch keeps its own copy of the allowed increases, so removing a row cannot change
       * what an existing batch resolves.
       */
      onRemoveRowAction: isEditEnabled ? props.onRemoveRowAction : undefined,
      emptyMessage: "No allowed increases have been added."
    };

    const dataTable = createDataTable<RentControlTypesFormLimit, TableState>(
      getColumns(),
      tableProps
    );

    return (
      <div>
        {yearOptions.length > 0 && (
          <div
            style={{
              marginBottom: "8px",
              display: "inline-block",
              minWidth: "150px"
            }}
          >
            <Dropdown
              onChange={setSelectedYear}
              options={yearOptions}
              selectedValue={selectedYear}
              placeholder="Filter by year"
            />
          </div>
        )}
        {dataTable}
      </div>
    );
  };

export default RentControlTypeLimitsFormTable;
